import IconButton from 'components/atoms/button/IconButton';
import Form from 'components/atoms/form/Form';
import Label from 'components/atoms/input/input-label/InputLabel';
import React, { FC, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ShowAllDocuments from './show-all-documents/ShowAllDocuments';
import { useAddPartnerFiles } from 'lib/hooks/mutations/Partners/useAddPartnerFiles';
import CreatableSelect from 'react-select/creatable';
import { customStyles } from 'lib/helpers/configs/customStyles';
import RadioButton from 'components/atoms/radio-button/RadioButton';
import Icon from 'components/atoms/icon/Icon';
import { IconType } from 'lib/helpers/constants/iconTypes';

interface Props {
    IsAddingDocument: boolean
    setIsAddingDocument: (value: boolean) => void
    setCurrentStep: (value: number) => void
    currentStep: number
    onConfirm: () => void
    data: any
}

const Tab3Documents: FC<Props> = ({ IsAddingDocument, setIsAddingDocument, onConfirm, data }) => {

    const { t } = useTranslation()

    const [Options, setOptions] = useState([
        { value: 'Application', label: t('Application') },
        { value: 'ID', label: t('ID') },
        { value: 'Passport', label: t('Passport') },
    ])

    const [inputValue, setInputValue] = useState('');

    const handleInputChange = (value: string) => {
        setInputValue(value);
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (inputValue) {
                setOptions([...Options, { value: inputValue, label: inputValue }])
            }
        }, 300); // Adjust delay as needed

        return () => clearTimeout(timeout);

        // eslint-disable-next-line
    }, [inputValue]);

    const { control,
        reset,
        handleSubmit,
        formState: { errors }
    } = useForm({
        defaultValues: {
            file: '',
            required: false,
        }
    })


    const handleReset = () => {
        reset()
        setOptions([
            { value: 'ID', label: t('ID') },
            { value: 'Passport', label: t('Passport') },
        ])
        setInputValue('')
    }

    const { mutateAsync, isLoading } = useAddPartnerFiles()

    const [AllData, setAllData] = useState<any>({
        files: data?.partner_files || []
    })

    const SubmitFiles = (data: any) => {

        setAllData({
            files: [...(AllData.files || []), { file: data.file.value, required: data.required }]
        });
        reset()
    }


    const SubmitForm = () => {

        const dataWithId = {
            ...AllData,
            partner_id: data.id
        }

        mutateAsync(dataWithId).then(() => {
            onConfirm()
        })

    }

    return (
        IsAddingDocument ? (
            <Form className="" onSubmit={handleSubmit(SubmitFiles)}>
                {/* Multiple Select for Files */}
                <div className="px-4 py-2 bg-[#F6FBFE] rounded-t-2xl text-xs sm:text-base ">
                    <div className="grid grid-cols-7 gap-3 font-inter-semibold text-primaryTextColor">
                        <div className="col-span-4">
                            {t('File Name')}
                        </div>
                        <div className="col-span-2">
                            <span className='hidden md:block'>
                                {t('Required')}?
                            </span>
                            <span className='block md:hidden'>
                                {t('Amount')}
                            </span>
                        </div>
                        <div className="col-span-1">
                            {t('Action')}
                        </div>
                    </div>
                </div>
                <div className="p-4 mb-5">
                    <div className="grid grid-cols-7 gap-3 items-center">
                        {AllData?.files?.length > 0 ? (
                            AllData?.files?.map((file: any, index: number) => (
                                <React.Fragment key={index}>
                                    <div className="col-span-4 text-primaryTextColor">
                                        {file.file}
                                    </div>
                                    <div className="col-span-2 text-primaryTextColor">
                                        {file.required ? 'Yes' : 'No'}
                                    </div>
                                    <div className="col-span-1 text-primaryTextColor">
                                        <div className="cursor-pointer" onClick={() => {
                                            setAllData({
                                                files: AllData.files?.filter((item: any) => item.file !== file.file)
                                            })
                                        }}>
                                            <Icon iconType={IconType.DELETE_ICON} />
                                        </div>
                                    </div>
                                </React.Fragment>
                            ))
                        ) : (
                            <>
                                <div className="col-span-4 text-primaryTextColor">-</div>
                                <div className="col-span-2 text-primaryTextColor">-</div>
                                <div className="col-span-1 text-primaryTextColor"></div>
                            </>
                        )}
                    </div>
                </div>
                <div className='rounded-[20px] bg-[#F6FBFE] p-4 flex flex-col gap-4'>
                    <div className="relative">
                        <Label text={t("File Name")} />
                        <Controller
                            name="file"
                            rules={{
                                required: t("This field is required"),
                                validate: (value: any) => {
                                    if (!AllData?.files) {
                                        return true
                                    }
                                    if (AllData?.files.some((file: any) => file.file === value.value)) {

                                        return t("This file already exists");
                                    }
                                    return true;
                                }
                            }}
                            control={control}
                            render={({ field }) => (
                                <CreatableSelect<any, false>
                                    {...field}
                                    placeholder={t('Choose or add files')}
                                    options={Options as any}
                                    onChange={(selectedOptions, actionMeta) => {
                                        field.onChange(selectedOptions); // Update form state

                                        if (actionMeta.action === "select-option") {
                                            setOptions([
                                                { value: 'Application', label: t('Application') },
                                                { value: 'ID', label: t('ID') },
                                                { value: 'Passport', label: t('Passport') },
                                            ])
                                        }
                                    }}
                                    onInputChange={(e) => { handleInputChange(e) }}
                                    className="cursor-pointer font-inter-regular react-select-container"
                                    classNamePrefix="react-select"
                                    styles={customStyles}
                                    isValidNewOption={() => false}
                                    menuPosition={"fixed"}
                                    menuPortalTarget={document.body}
                                />
                            )}
                        />
                        {errors.file?.message && (
                            <div className="font-inter-regular text-xs text-red-600 w-max absolute bottom-[-16px] 2xl:bottom-[-20px]  left-[2px]">
                                {errors.file?.message as string}
                            </div>
                        )}
                    </div>
                    <div>

                        <div className="col-span-2">
                            <Label>{t('Is Required')}?</Label>
                        </div>
                        <div className="grid grid-cols-2 gap-3">

                            <div className='col-span-2 sm:col-span-1'>
                                <Controller
                                    name={`required`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioButton
                                            label={t('Yes')}
                                            isChecked={field.value === true}
                                            value={'Yes'}
                                            onChange={() => { field.onChange(true); }}
                                            labelClassName="h-[41px] 2xl:h-[46px] items-center flex bg-white"
                                            iconWrapperClassName="!top-[20px] md:!top-[19px] 2xl:!top-[22px] !right-[6px]"
                                            className="right-[8px]"
                                        />
                                    )}
                                />
                            </div>
                            <div className='col-span-2 sm:col-span-1'>
                                <Controller
                                    name={`required`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioButton
                                            label={t('No')}
                                            isChecked={field.value === false}
                                            value={'No'}
                                            onChange={() => { field.onChange(false) }}
                                            labelClassName="h-[41px] 2xl:h-[46px] items-center flex bg-white"
                                            iconWrapperClassName="!top-[20px] md:!top-[19px] 2xl:!top-[22px] !right-[6px]"
                                            className="right-[8px]"
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                </div>
                <div className="flex justify-end pt-5">
                    <button className='flex items-center gap-2'>
                        <Icon iconType={IconType.PLUS_ICON} color='var(--theme)' />
                        <span className='text-theme font-inter-medium text-sm'>{t('Add File')} </span>
                    </button>
                </div>
                <div className="pt-14 pb-5 flex justify-end gap-3">
                    <IconButton secondary className="max-w-[185px] w-full"
                        onClick={handleReset}
                        type='button'
                        disabled={isLoading}
                    >
                        {t('Reset')}
                    </IconButton>
                    <IconButton className="max-w-[185px] w-full" type='button' onClick={SubmitForm}
                        disabled={isLoading || AllData.files.length === 0 || AllData.files === data?.partner_files}
                    >
                        {t('Submit')}
                    </IconButton>
                </div>

            </Form >
        ) : (
            <ShowAllDocuments setIsAddingDocument={setIsAddingDocument} data={data} />
        )

    )
}

export default Tab3Documents